import { useState, useEffect } from 'react'
import { useBrowserInfo } from './useBrowserInfo'

interface useDisplayOptimalityResult {
    isOptimal: boolean
    isBrowserSupported: boolean
    isWindowLargeEnough: boolean
}

/**
 * Custom hook for checking display area optimality
 * @param {number} minWidth - Minimum required width (default: 1402)
 * @param {number} minHeight - Minimum required height (default: 707)
 * @returns {Object} Object containing isOptimal boolean and checkDisplayOptimality function
 */
export const useDisplayOptimality = (minWidth: number = 1402, minHeight: number = 707): useDisplayOptimalityResult => {
    const [isBrowserSupported, setIsBrowserSupported] = useState(true)
    const [isWindowLargeEnough, setIsWindowLargeEnough] = useState(true)
    const browserInfo = useBrowserInfo()

    useEffect(() => {
        const checkDisplayOptimality = () => {
            // Safari is never optimal
            setIsBrowserSupported(!browserInfo.isSafari)
            
            // Check if display area meets minimum requirements
            const visualViewport: VisualViewport | null = window.visualViewport
            if (visualViewport !== null) {
                setIsWindowLargeEnough(visualViewport.width >= minWidth && visualViewport.height >= minHeight)
            }
        }

        checkDisplayOptimality()
        
        // Listen for display changes
        window.addEventListener('resize', checkDisplayOptimality)
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', checkDisplayOptimality)
        }
        
        return () => {
            window.removeEventListener('resize', checkDisplayOptimality)
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', checkDisplayOptimality)
            }
        }
    }, [browserInfo, minWidth, minHeight])

    return { 
        isOptimal: isBrowserSupported && isWindowLargeEnough,
        isBrowserSupported,
        isWindowLargeEnough
    }
}
